/**
 * WCO-3271: Session/add-material lot badge helpers — type from lot_type, lifecycle from status.
 * Legacy linked_lot_status / status fallbacks for unmigrated rows only.
 */

export type LotMaterialBadge = { label: string; cls: string };

const KNOWN_LOT_TYPES = new Set(['raw', 'remainder', 'fp_ni', 'by_product_p', 'by_product_m']);

export function resolveLotType(
  lotType?: string | null,
  legacyStatus?: string | null,
): string {
  if (lotType && KNOWN_LOT_TYPES.has(lotType)) return lotType;
  const status = legacyStatus ?? '';
  if (status.startsWith('finish_product_ni')) return 'fp_ni';
  if (status === 'remainder') return 'remainder';
  if (status === 'by_product_p' || status === 'by_product') return 'by_product_p';
  if (status === 'by_product_m') return 'by_product_m';
  return 'raw';
}

export function isFpNiCarryoverMaterial(material: {
  linked_lot_type?: string | null;
  linked_lot_status?: string | null;
}): boolean {
  return material.linked_lot_type === 'fp_ni'
    || (material.linked_lot_status?.startsWith('finish_product_ni') ?? false);
}

export function getLotMaterialBadge(
  opts: {
    lotType?: string | null;
    status?: string | null;
    legacyStatus?: string | null;
    isNiRemnant?: boolean | null;
    /** Session details: raw lots from the original transfer → Transfer. Add-material picker: → Intact. */
    rawBadge?: 'transfer' | 'intact';
  },
  t: (key: string) => string,
): LotMaterialBadge | null {
  const { isNiRemnant } = opts;
  if (isNiRemnant === undefined || isNiRemnant === null) return null;

  if (isNiRemnant) {
    return {
      label: t('sessionDetails.lotNotIntact') || 'Not-intact',
      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    };
  }

  const lotType = resolveLotType(opts.lotType, opts.legacyStatus ?? opts.status);
  const lifecycle = opts.status ?? 'available';

  if (lotType === 'by_product_p') {
    return {
      label: t('sessionDetails.lotByProduct') || 'By-product',
      cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    };
  }
  if (lifecycle === 'on_hold') {
    return {
      label: t('sessionDetails.lotOnHold') || 'On-hold',
      cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    };
  }
  if (lotType === 'remainder') {
    return {
      label: t('sessionDetails.lotIntact') || 'Intact',
      cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    };
  }
  if (opts.rawBadge === 'intact') {
    return {
      label: t('sessionDetails.lotIntact') || 'Intact',
      cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    };
  }
  return {
    label: t('sessionDetails.lotTransfer') || 'Transfer',
    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  };
}
